import React from "react";
import styled from "styled-components";

/** Phrases that refer to the bids / vendor-quotes UI (longest match first). */
const BIDS_PANEL_LINK_RE =
  /\*\*Bids\*\* panel \(Vendor Quotes tab\)|\*\*Bids\*\* panel|Bids panel \(Vendor Quotes tab\)|Vendor Quotes tab|Vendor quotes tab|Vendor Quotes|Vendor quotes|Bids panel|Bids tab|bid tab|bid window|quotes tab/gi;

function bidsPanelButtonLabel(matched) {
  return matched.replace(/\*\*/g, "");
}

export const BidsPanelInlineButton = styled.button`
  display: inline;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  color: var(--color-action, #e85d2a);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  vertical-align: baseline;

  &:hover {
    color: #c2410c;
  }

  &:focus-visible {
    outline: 2px solid var(--color-action, #e85d2a);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

function linkifyBidsPanelPhrases(node, onOpenBidsPanel, keyPrefix = "bpl") {
  if (!onOpenBidsPanel) return node;
  if (typeof node === "string") {
    const out = [];
    let last = 0;
    let m;
    let k = 0;
    const re = new RegExp(BIDS_PANEL_LINK_RE.source, BIDS_PANEL_LINK_RE.flags);
    while ((m = re.exec(node)) !== null) {
      if (m.index > last) {
        out.push(node.slice(last, m.index));
      }
      const label = m[0];
      out.push(
        <BidsPanelInlineButton
          key={`${keyPrefix}-${k++}`}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onOpenBidsPanel();
          }}
        >
          {label}
        </BidsPanelInlineButton>
      );
      last = re.lastIndex;
    }
    if (out.length === 0) return node;
    if (last < node.length) {
      out.push(node.slice(last));
    }
    return out;
  }
  if (Array.isArray(node)) {
    return node.map((child, i) =>
      linkifyBidsPanelPhrases(child, onOpenBidsPanel, `${keyPrefix}-${i}`)
    );
  }
  return node;
}

/**
 * Turns `**phrase**` from the model into real bold in the UI (saves space vs. raw asterisks).
 * Newlines stay in plain string slices; parent should use `white-space: pre-wrap`.
 */
export function formatChatBoldSegments(text) {
  if (text == null || text === "") return null;
  const pattern = /\*\*(.+?)\*\*/g;
  const out = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    out.push(<strong key={`md-b-${k++}`}>{m[1]}</strong>);
    last = pattern.lastIndex;
  }
  if (out.length === 0) return text;
  if (last < text.length) {
    out.push(text.slice(last));
  }
  return out;
}

/**
 * Bold segments plus optional inline buttons for bids / vendor-quotes references (mobile chat).
 */
/**
 * Split plain text into bold segments and inline bids-panel buttons (mobile).
 * Linkify runs on raw text first so "**Bids** panel" still matches.
 */
export function formatChatMessageSegments(
  text,
  { linkifyBidsPanel = false, onOpenBidsPanel } = {}
) {
  if (text == null || text === "") return null;
  if (!linkifyBidsPanel || !onOpenBidsPanel) {
    return formatChatBoldSegments(text);
  }

  const re = new RegExp(BIDS_PANEL_LINK_RE.source, BIDS_PANEL_LINK_RE.flags);
  const parts = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push({ kind: "text", value: text.slice(last, m.index) });
    }
    parts.push({ kind: "bids", value: m[0] });
    last = m.index + m[0].length;
  }
  if (parts.length === 0) {
    return formatChatBoldSegments(text);
  }
  if (last < text.length) {
    parts.push({ kind: "text", value: text.slice(last) });
  }

  return parts.flatMap((part, i) => {
    if (part.kind === "bids") {
      return (
        <BidsPanelInlineButton
          key={`chat-bids-${i}`}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onOpenBidsPanel();
          }}
        >
          {bidsPanelButtonLabel(part.value)}
        </BidsPanelInlineButton>
      );
    }
    const bold = formatChatBoldSegments(part.value);
    if (bold == null || bold === "") return [];
    return Array.isArray(bold)
      ? bold.map((node, j) =>
          React.isValidElement(node)
            ? React.cloneElement(node, { key: `chat-b-${i}-${j}-${k++}` })
            : node
        )
      : [bold];
  });
}
